import type { ContentMatch, NodeType, Schema } from '@atlaskit/editor-prosemirror/model';

import type { AdfParserConfig, ContentNode, LiftedNode } from './types';
import { LIFT_MARKER } from './types';

export interface RepairStrategy {
	/**
	 * Apply in-place mutations (fix data types before structural repairs)
	 */
	applyNodeMutations: (node: ContentNode) => boolean;

	/**
	 * Set the ADF parser configuration
	 */
	setAdfParserConfig: (config: AdfParserConfig) => void;

	/**
	 * Check if a structurally valid but misplaced node needs fixing (e.g., tableRow at doc level)
	 */
	shouldAttemptStructuralFix: (node: ContentNode, parentType: string | undefined) => boolean;

	/**
	 * Attempt to repair an invalid node to make it valid in the given parent context.
	 * @param siblings - The current content array, allows checking/modifying previous nodes
	 * @param isInvalid - True if the node is invalid in the current parent context
	 * @returns Repaired node(s), LiftedNode to bubble up, empty array if consumed, or null if repair is not possible/desired
	 */
	tryRepair: (
		node: ContentNode,
		parentType: string | undefined,
		siblings: ContentNode[],
		isInvalid: boolean,
	) => ContentNode | ContentNode[] | LiftedNode | null;
}

// ADF Repair Strategy - Attempts common repairs for LLM-generated content
export class AdfRepairStrategy implements RepairStrategy {
	private static readonly LIST_NODE_TYPES = new Set(['bulletList', 'orderedList', 'taskList']);
	private adfParserConfig: AdfParserConfig = {};

	// eslint-disable-next-line no-useless-constructor
	constructor(private readonly schema: Schema) { }

	setAdfParserConfig(config: AdfParserConfig): void {
		this.adfParserConfig = config;
	}

	private getNodeType(nodeType: string): NodeType | null {
		const $nodeType = this.schema.nodes[nodeType];
		return $nodeType || null;
	}

	private wrapNodeWithType(node: ContentNode, $nodeType: NodeType): ContentNode | null {
		const $content = this.schema.nodeFromJSON(node);
		const $newContent = $nodeType.createAndFill(null, $content);
		return $newContent?.toJSON() ?? null;
	}

	private wrapNode(
		node: ContentNode,
		$nodeType: NodeType,
		$parentNodeType: NodeType,
		siblings: ContentNode[],
	): ContentNode | null {
		const contentMatch = this.findContentMatchAtPosition($parentNodeType, siblings);
		const wrappers = contentMatch?.findWrapping($nodeType);
		if (!wrappers || wrappers.length === 0) {
			return null;
		}

		let $content = this.schema.nodeFromJSON(node);

		for (let i = wrappers.length - 1; i >= 0; i--) {
			const wrapper = wrappers[i];
			const match = wrapper?.contentMatch?.matchType($content.type);
			if (!match || !match.validEnd) {
				return null;
			}
			const $newContent = wrapper?.createAndFill(null, $content);
			if (!$newContent) {
				return null;
			}
			$content = $newContent;
		}

		return $content.toJSON();
	}

	// Convert a mark node (e.g., strong, em) to a proper text node with marks
	private convertMarkNodeToMark(
		node: ContentNode,
		$parentNodeType: NodeType,
	): ContentNode | ContentNode[] {
		// ProseMirror doesn't allow empty text nodes, use space as fallback
		const text = node.text && node.text.length > 0 ? node.text : ' ';
		const $textNode = this.schema.text(text, [this.schema.mark(node.type)]);

		if ($parentNodeType.isTextblock) {
			/**
			 * To produce valid ADF, we need to use JSON Transformer
			 * However, it will slow down the repair process
			 * So, we are ignoring it for now
			 */
			return $textNode.toJSON();
		}

		// Convert to JSON before wrapping (don't pass the original mark-as-node)
		const textNodeJson = $textNode.toJSON();
		const wrapped = this.wrapNode(textNodeJson, $textNode.type, $parentNodeType, []);
		return wrapped ?? textNodeJson;
	}

	private isTableCellNode(node: ContentNode): boolean {
		return node.type === 'tableCell' || node.type === 'tableHeader';
	}

	private isListNode(node: ContentNode): boolean {
		return AdfRepairStrategy.LIST_NODE_TYPES.has(node.type);
	}

	// Block containers that should be lifted to parent level when invalid in current context
	private static readonly LIFTABLE_BLOCK_CONTAINERS = new Set([
		'panel',
		'blockquote',
		'expand',
		'nestedExpand',
		'table',
		'codeBlock',
		'bodiedExtension',
		'extensionFrame',
	]);

	private isLiftableBlockContainer(nodeType: string): boolean {
		return AdfRepairStrategy.LIFTABLE_BLOCK_CONTAINERS.has(nodeType);
	}

	// Simple containers: when an invalid liftable node is encountered, close the container
	// and lift everything (the invalid node + remaining siblings)
	private static readonly SIMPLE_CONTAINERS = new Set([
		'panel',
		'blockquote',
		'expand',
		'nestedExpand',
		'bodiedExtension',
	]);

	// Compound nodes require multiple parts to function (list needs listItems, table needs rows/cells)
	// When lifting from these, we just accumulate and insert later, don't close the parent
	private isSimpleContainer(parentType: string | undefined): boolean {
		return parentType ? AdfRepairStrategy.SIMPLE_CONTAINERS.has(parentType) : false;
	}

	// Append node to last sibling if it matches targetType, otherwise null
	private appendToLastSibling(
		node: ContentNode,
		siblings: ContentNode[],
		targetType: string,
	): ContentNode[] | null {
		const lastSibling = siblings[siblings.length - 1];
		if (lastSibling && lastSibling.type === targetType) {
			if (!lastSibling.content) {
				lastSibling.content = [];
			}
			lastSibling.content.push(node);
			return []; // Node consumed
		}
		return null;
	}

	/**
	 * Generic wrapper function that tries to append a node to existing structures or creates a new wrapper.
	 *
	 * @param node - The node to wrap/append
	 * @param siblings - Sibling nodes to check
	 * @param options - Configuration for how to append/wrap
	 * @returns The wrapped node or empty array if consumed
	 */
	private wrapOrAppendNode(
		node: ContentNode,
		siblings: ContentNode[],
		options: {
			// If true, also check one level deep in last sibling's content (e.g., expand > bulletList)
			checkNestedParent?: boolean;
			// If nestedParentType is specified and last content doesn't match, create new nested parent
			createNestedIfMissing?: boolean;
			// Default wrapper to create if no match found
			defaultWrapper: ContentNode;
			// Types to check at sibling level (e.g., 'bulletList' or ['bulletList', 'orderedList'])
			directParentTypes: string | string[];
			// Type for the nested parent to append to (e.g., 'tableRow' for tableCell within table)
			nestedParentType?: string;
		},
	): ContentNode | ContentNode[] {
		const lastSibling = siblings[siblings.length - 1];
		const parentTypes = Array.isArray(options.directParentTypes)
			? options.directParentTypes
			: [options.directParentTypes];

		if (lastSibling) {
			// Check for direct parent match at sibling level
			if (parentTypes.includes(lastSibling.type)) {
				// Handle nested parent type (e.g., table > tableRow for tableCell)
				if (options.nestedParentType) {
					if (!lastSibling.content) {
						lastSibling.content = [];
					}

					if (lastSibling.content.length > 0) {
						const lastContent = lastSibling.content[lastSibling.content.length - 1];
						if (lastContent && lastContent.type === options.nestedParentType) {
							// Found nested parent - append to it
							if (!lastContent.content) {
								lastContent.content = [];
							}
							lastContent.content.push(node);
							return []; // Node consumed
						}
					}

					// Nested parent not found but createNestedIfMissing is true
					if (options.createNestedIfMissing) {
						lastSibling.content.push({ type: options.nestedParentType, content: [node] });
						return []; // Node consumed
					}
				} else {
					// No nested parent type - append directly
					if (!lastSibling.content) {
						lastSibling.content = [];
					}
					lastSibling.content.push(node);
					return []; // Node consumed
				}
			}

			// Check one level deep for parent match (e.g., expand > bulletList for listItem)
			if (
				options.checkNestedParent &&
				lastSibling.content &&
				Array.isArray(lastSibling.content) &&
				lastSibling.content.length > 0
			) {
				const lastContent = lastSibling.content[lastSibling.content.length - 1];
				if (lastContent && parentTypes.includes(lastContent.type)) {
					if (!lastContent.content) {
						lastContent.content = [];
					}
					lastContent.content.push(node);
					return []; // Node consumed
				}
			}
		}

		// No match found - return default wrapper
		return options.defaultWrapper;
	}

	private appendToLastTableRow(node: ContentNode, siblings: ContentNode[]): ContentNode[] | null {
		return this.appendToLastSibling(node, siblings, 'tableRow');
	}

	private wrapTableRow(node: ContentNode, siblings: ContentNode[]): ContentNode | ContentNode[] {
		return this.wrapOrAppendNode(node, siblings, {
			directParentTypes: 'table',
			defaultWrapper: { type: 'table', attrs: { isNumberColumnEnabled: false }, content: [node] },
		});
	}

	private wrapListItem(node: ContentNode, siblings: ContentNode[]): ContentNode | ContentNode[] {
		return this.wrapOrAppendNode(node, siblings, {
			directParentTypes: ['bulletList', 'orderedList'],
			checkNestedParent: true, // Check expand > bulletList
			defaultWrapper: { type: 'bulletList', content: [node] },
		});
	}

	private wrapTaskItem(node: ContentNode, siblings: ContentNode[]): ContentNode | ContentNode[] {
		return this.wrapOrAppendNode(node, siblings, {
			directParentTypes: 'taskList',
			checkNestedParent: true, // Check expand > taskList
			defaultWrapper: { type: 'taskList', attrs: { localId: null }, content: [node] },
		});
	}

	private wrapTableCell(node: ContentNode, siblings: ContentNode[]): ContentNode | ContentNode[] {
		return this.wrapOrAppendNode(node, siblings, {
			directParentTypes: 'table',
			nestedParentType: 'tableRow', // table > tableRow > tableCell
			createNestedIfMissing: true, // Create tableRow if table exists but no tableRow
			defaultWrapper: {
				type: 'table',
				attrs: { isNumberColumnEnabled: false },
				content: [{ type: 'tableRow', content: [node] }],
			},
		});
	}

	shouldAttemptStructuralFix(node: ContentNode, parentType: string | undefined): boolean {
		if (parentType === 'table') {
			// Only attempt structural fix (breaking out) if it's NOT a table part.
			// Table cells/headers are "invalid" but should be handled by wrapping, not breaking.
			return node.type !== 'tableRow' && !this.isTableCellNode(node);
		}
		// Handle malformed table nesting - break out when:
		// 1. tableCell/tableHeader contains a tableRow (cell should have ended)
		// 2. tableRow contains non-cell content (row should have ended)
		else if (
			(this.isTableCellNode({ type: parentType || '' }) && node.type === 'tableRow') ||
			(parentType === 'tableRow' && !this.isTableCellNode(node))
		) {
			return true;
		}

		return parentType === 'doc' && (node.type === 'tableRow' || this.isTableCellNode(node));
	}

	private isMarkWithOutAttrs(nodeType: string): boolean {
		const mark = this.schema.marks[nodeType];
		if (!mark) {
			return false;
		}
		return !mark.spec.attrs || Object.keys(mark.spec.attrs).length === 0;
	}

	private findContentMatchAtPosition(
		$parentNodeType: NodeType,
		siblings: ContentNode[],
	): ContentMatch | null {
		let currentMatch: ContentMatch | null = $parentNodeType.contentMatch;
		for (const sibling of siblings) {
			if (!currentMatch) {
				return null;
			}
			const siblingType = this.getNodeType(sibling.type);
			currentMatch = siblingType ? currentMatch.matchType(siblingType) : null;
		}
		return currentMatch;
	}

	private isTypeAllowedAt(
		$parentNodeType: NodeType,
		siblings: ContentNode[],
	): (type: NodeType) => boolean {
		return ($nodeType) =>
			this.findContentMatchAtPosition($parentNodeType, siblings)?.matchType($nodeType) !== null;
	}

	private wrappingMap: [string, string][] = [
		// NOTE: listItem and taskItem have special handling in switch statement below for intelligent append
		['paragraph', 'blockTaskItem'],
		['listItem', 'bulletList'],
		['taskItem', 'taskList'],
	];

	applyNodeMutations(node: ContentNode): boolean {
		let wasMutated = false;

		// Infer missing type from node properties
		if (!node.type && node.text !== undefined) {
			node.type = 'text';
			wasMutated = true;
		}

		// If still no type, can't proceed with type-specific mutations
		if (!node.type) {
			return wasMutated;
		}

		switch (node.type) {
			case 'date':
				const today = new Date().getTime().toString();
				node.attrs = node.attrs || {};
				if (node.attrs.timestamp) {
					if (
						typeof node.attrs.timestamp === 'string' &&
						// eslint-disable-next-line require-unicode-regexp
						!node.attrs.timestamp.match(/^\d+$/)
					) {
						const d = new Date(node.attrs.timestamp);
						node.attrs.timestamp = isNaN(d.getTime()) ? today : d.getTime().toString();
						return true;
					}
				} else {
					node.attrs.timestamp = today;
					return true;
				}
				break;
		}

		if (this.adfParserConfig.localIdTrimming) {
			if (node.attrs && node.attrs.localId && typeof node.attrs.localId === 'string') {
				node.attrs.localId = node.attrs.localId.substring(0, 36);
				return true;
			}
		}

		return wasMutated;
	}

	tryRepair(
		node: ContentNode,
		parentType: string | undefined,
		siblings: ContentNode[],
		isInvalid: boolean = false,
	): ContentNode | ContentNode[] | LiftedNode | null {
		// Apply mutations first (in-place data type fixes, including type inference)
		const wasMutated = this.applyNodeMutations(node);

		// If still no type after mutations, can't repair
		if (!node.type) {
			return null;
		}

		let $parentNodeType: NodeType | undefined;
		if (parentType) {
			$parentNodeType = this.schema.nodes[parentType];
		}

		// Handle marks (strong, em, etc.) appearing as nodes FIRST
		// before trying to access as node types
		if (this.isMarkWithOutAttrs(node.type)) {
			return this.convertMarkNodeToMark(node, $parentNodeType || this.schema.topNodeType);
		}

		const $nodeType = this.schema.nodes[node.type];
		if (!$nodeType) {
			return null;
		}

		if (parentType && $parentNodeType) {
			// Wrap orphaned inline nodes in paragraphs where allowed
			if ($nodeType.isInline) {
				const wrapped = this.wrapNode(node, $nodeType, $parentNodeType, siblings);
				if (wrapped) {
					return wrapped;
				}
			}

			// Unwrap textblocks nested inside other textblocks
			// paragraph -> taskItem, heading -> paragraph, etc.
			if ($parentNodeType.isTextblock && $nodeType.isTextblock) {
				return node.content || null;
			}

			// Handle block containers (panel, blockquote, expand, table) when invalid in current context
			// For simple containers (panel, blockquote): close container and lift all remaining
			// For compound nodes (listItem, tableCell): just accumulate and insert later
			if (this.adfParserConfig.liftInvalidBlockNodes && isInvalid && this.isLiftableBlockContainer(node.type)) {
				return {
					[LIFT_MARKER]: true,
					node,
					closeContainer: this.isSimpleContainer(parentType),
				};
			}

			const isTypeAllowed = this.isTypeAllowedAt($parentNodeType, siblings);

			const $nestedExpandType = this.schema.nodes['nestedExpand'];
			if ($nestedExpandType && node.type === 'expand' && isTypeAllowed($nestedExpandType)) {
				return { ...node, type: 'nestedExpand' };
			}

			const $tableType = this.schema.nodes['table'];
			if ($tableType && this.isTableCellNode(node) && isTypeAllowed($tableType)) {
				return this.wrapTableCell(node, siblings);
			}

			// Handle special cases that need custom logic
			switch (parentType) {
				case 'doc':
					if (node.type === 'tableRow') {
						return this.wrapTableRow(node, siblings);
					}
					if (node.type === 'listItem') {
						return this.wrapListItem(node, siblings);
					}
					if (node.type === 'taskItem') {
						return this.wrapTaskItem(node, siblings);
					}
					break;

				case 'expand':
				case 'nestedExpand':
					if (node.type === 'listItem') {
						return this.wrapListItem(node, siblings);
					}
					if (node.type === 'taskItem') {
						return this.wrapTaskItem(node, siblings);
					}
					break;

				case 'table':
					if (this.isTableCellNode(node)) {
						return this.appendToLastTableRow(node, siblings);
					}
					break;

				case 'bulletList':
				case 'orderedList':
					if (this.isListNode(node)) {
						return { type: 'listItem', content: [node] };
					} else {
						const wrapped = this.wrapNode(node, $nodeType, $parentNodeType, siblings);
						if (wrapped) {
							return wrapped;
						}
					}
					break;

				case 'paragraph':
					// bulletList/orderedList/taskList nodes cannot be children of paragraph
					// Return as array to signal unwrap operation (will replace paragraph with bulletList)
					if (this.isListNode(node)) {
						return [node];
					}
					break;
			}

			// Generic wrapping fallback
			for (const [nodeType, wrapperType] of this.wrappingMap) {
				const $wrapperType = this.schema.nodes[wrapperType];
				if ($wrapperType && node.type === nodeType && isTypeAllowed($wrapperType)) {
					return this.wrapNodeWithType(node, $wrapperType);
				}
			}
		}

		// Return mutated node if no structural repair was needed
		return wasMutated ? node : null;
	}
}
